// oracle-storage-operations.ts
import { Observable, Subject, from, throwError } from "rxjs";
import { map, catchError, filter, takeUntil, tap } from "rxjs/operators";
import { HttpClient, HttpEventType, HttpResponse } from "@angular/common/http";
import { IStorageOperations } from "./storage-operations.interface";
import { StorageOracleService } from "src/app/core/services/storage-oracle.service";
import { HeaderService } from "src/app/core/services/header.service";
import { ManageStorageLogsService } from "src/app/core/services/manage-storage-logs.service";
import { 
  StorageItem, 
  UploadProgress, 
  OperationResult, 
  FileOperationContext, 
  DownloadResult,
  UploadContext, 
  ProgressCallback
} from "src/app/core/models/storage-models.interface";

export class OracleStorageOperations implements IStorageOperations {
  
  constructor(
    private oracleService: StorageOracleService,
    private headerService: HeaderService,
    private storageLogService: ManageStorageLogsService
  ) {}
  
  listFiles(path: string, context: FileOperationContext): Observable<StorageItem[]> {
    return this.oracleService.getStorageData(context.deploymentName, context.currentStorageName, path)
      .pipe(
        map((data: any[]) => data.map(item => ({
          name: item.name,
          type: item.type === 'directory' ? 'directory' as const : 'file' as const,
          path: context.defaultCurrentPath + `/${item.name}`,
          owner: item.owner,
          date: item.date ? new Date(item.date) : undefined,
          size: item.size,
          lastModified: item.date ? new Date(item.date) : undefined
        }))),
        catchError(error => throwError(() => new Error(`Failed to list Oracle Cloud objects: ${error.message}`)))
      );
  }
  
  uploadFile(
    file: File, 
    destinationPath: string, 
    context: FileOperationContext,
    uniqueId: number,
    uploadContext?: UploadContext
  ): Observable<UploadProgress> {
    return new Observable<UploadProgress>(observer => {
      const fullPath = destinationPath + `/${file.name}`;
      const timeStarted = this.headerService.getUserNewDate();
      
      // Create cancellation subject  
      const cancelSubject = new Subject();
      this.storageLogService.onPremiseUploadRequests[uniqueId] = cancelSubject;
      
      this.oracleService.uploadObject(fullPath, file, context.currentStorageName)
        .pipe(
          filter((event) => !!event),
          takeUntil(cancelSubject)
        )
        .subscribe({
          next: (event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              const percentage = Math.round((event.loaded / event.total) * 100);
              const timeElapsed = this.headerService.getUserNewDate().valueOf() - timeStarted.valueOf();
              const uploadSpeed = event.loaded / (timeElapsed / 1000);
              const timeRemaining = Math.round((file.size - event.loaded) / uploadSpeed);
              
              // Update storage logs
              this.storageLogService.updateTimeRemaining(uniqueId, isFinite(timeRemaining) ? timeRemaining : 0);
              this.storageLogService.updateStatusOfLog(uniqueId, `Uploaded ${percentage}%`);
              this.storageLogService.updateStorageLogsStore();
              
              observer.next({
                loaded: event.loaded,
                total: event.total,
                percentage,
                timeRemaining: isFinite(timeRemaining) ? timeRemaining : undefined,
                uploadSpeed
              });
            } else if (event instanceof HttpResponse) {
              // Upload completed
              this.storageLogService.updateTimeRemaining(uniqueId, null);
              this.storageLogService.updateRequestCompleteFields(uniqueId, "Upload Completed");
              
              observer.next({ loaded: file.size, total: file.size, percentage: 100 });
              observer.complete();
            }
          },
          error: (error) => {
            this.storageLogService.updateTimeRemaining(uniqueId, null);
            this.storageLogService.updateRequestCompleteFields(uniqueId, "Upload Failed");
            observer.error(error);
          }
        });
    });
  }
  
  downloadFile(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<DownloadResult> {
    const fileName = filePath.split('/').pop() || '';
    const dirName = filePath.substring(0, filePath.lastIndexOf('/'));
    
    if (uniqueId) {
      this.storageLogService.updateStatusOfLog(uniqueId, `Download started for ${fileName}`);
    }
    
    const url = this.oracleService.downloadFile(context.currentStorageName, `${dirName}/${fileName}`);
    
    return new Observable<DownloadResult>(observer => {
      if (uniqueId) {
        this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Completed");
      }
      
      observer.next({ 
        url: url, 
        directDownload: false,
        fileName: fileName
      });
      observer.complete();
    });
  }
  
  deleteFile(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
    const fileName = filePath.split('/').pop() || '';
    const isDirectory = !fileName.includes('.');
    
    // For directories, add trailing slash
    const deletePath = isDirectory ? `${filePath}/` : filePath;
    
    return this.oracleService.deleteObject(context.deploymentName, context.currentStorageName, deletePath)
      .pipe(
        map(() => ({ success: true, message: 'File deleted successfully' })),
        catchError(error => throwError(() => new Error(`Oracle Cloud delete failed: ${error.message}`)))
      );
  }

  
  createDirectory(directoryName: string, path: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
    let processedDirName = directoryName;
    
    if (path !== ".") {
      processedDirName = path + "/" + directoryName;
    }
    
    return this.oracleService.createFolder(processedDirName, context.currentStorageName).pipe(
      map(() => ({ success: true, message: 'Oracle Cloud directory created successfully' })),
      tap(() => {
        if (uniqueId) {
          this.storageLogService.updateRequestCompleteFields(uniqueId, "Completed");
        }
      }),
      catchError(error => {
        if (uniqueId) {
          this.storageLogService.updateRequestCompleteFields(uniqueId, "Failed");
        }
        return throwError(() => new Error(`Oracle Cloud directory creation failed: ${error.message}`));
      })
    );
  }
  
  copyFile(sourcePath: string, destinationPath: string, context: FileOperationContext): Observable<OperationResult> {
    return throwError(() => new Error('Copy operation not implemented for Oracle Cloud'));
  }
  
  renameFile(oldPath: string, newName: string, context: FileOperationContext): Observable<OperationResult> {
    return throwError(() => new Error('Rename operation not implemented for Oracle Cloud'));
  }
  
  checkFileExists(filePath: string, context: FileOperationContext): Observable<boolean> {
    return this.listFiles(filePath.substring(0, filePath.lastIndexOf('/')), context)
      .pipe(
        map(files => {
          const fileName = filePath.split('/').pop();
          return files.some(file => file.name === fileName);
        }),
        catchError(() => throwError(() => false))
      );
  }
  
  readFileContent(filePath: string, context: FileOperationContext): Observable<string> {
    const fileName = filePath.split('/').pop() || '';
    const dirName = filePath.substring(0, filePath.lastIndexOf('/'));
    
    return this.oracleService.viewFileData(context.currentStorageName, `${dirName}/${fileName}`)
      .pipe(
        catchError(error => throwError(() => new Error(`Failed to read Oracle Cloud file content: ${error.message}`)))
      );
  }
  
  saveFileContent(filePath: string, content: string | File, context: FileOperationContext): Observable<OperationResult> {
    if (content instanceof File) {
      return this.uploadFile(content, filePath, context, Date.now()).pipe(
        map(() => ({ success: true, message: 'File saved successfully' }))
      );
    } else {
      const fileName = filePath.split('/').pop() || 'file.txt';
      const file = new File([content], fileName, { type: 'text/plain' });
      
      return this.uploadFile(file, filePath, context, Date.now()).pipe(
        map(() => ({ success: true, message: 'File content saved successfully' }))
      );
    }
  }

  cancelOperation(uniqueId: number, operationType: 'upload' | 'download' | 'zip' | 'unzip' | 'general'): boolean {
    switch (operationType) {
      case 'upload':
        this.storageLogService.cancelOracleUpload(uniqueId);
        return true;
      case 'download':
        this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Cancelled");
        return true;
      case 'zip':
      case 'unzip':
        this.storageLogService.updateRequestCompleteFields(uniqueId, `${operationType} not supported`);
        return false;
      default:
        this.storageLogService.updateRequestCompleteFields(uniqueId, "Operation Cancelled");
        return true;
    }
  }

  verifyDirectoryPath(paths: string[], context: FileOperationContext): Observable<boolean> {
    return new Observable<boolean>(observer => {
      // Convert paths to full directory paths for Oracle
      const directoryPaths = paths.map(path => {
        // For Oracle, we need to create the full path: defaultPath + "/" + directoryPath
        const fullPath = context.currentPath === '.' ? path : `${context.currentPath}/${path}`;
        return fullPath;
      });

      Promise.all(
        directoryPaths.map(fullPath => {
          // Oracle creates directories by creating folders
          return this.oracleService.createFolder(fullPath, context.currentStorageName).toPromise();
        })
      ).then(() => {
        observer.next(true);
        observer.complete();
      }).catch(error => {
        observer.next(true); // Continue anyway
        observer.complete();
      });
    });
  }

  viewFileData(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<any> {
    const fileName = filePath.split('/').pop() || '';
    const dirName = filePath.substring(0, filePath.lastIndexOf('/'));
    
    return this.oracleService.viewFileData(context.currentStorageName, `${dirName}/${fileName}`)
      .pipe(
        catchError(error => throwError(() => new Error(`Failed to view Oracle Cloud file: ${error.message}`)))
      );
  }

  generateDownloadUrl(filePath: string, context: FileOperationContext): Observable<string> {
    let processedPath = this.processPath(filePath, context);
    const url = this.oracleService.downloadFile(context.currentStorageName, processedPath);
    return new Observable<string>(observer => {
      observer.next(url);
      observer.complete();
    });
  }

  private processPath(filePath: string, context: FileOperationContext): string {
    let dirName = context.currentPath
      .replace("/mnt/syncstore", "")
      .replace("/mnt/workstore", "");
    if (dirName.substring(0, 1) == "/") dirName = dirName.substring(1);
    
    const fileName = filePath.split('/').pop() || '';
    return dirName ? `${dirName}/${fileName}` : fileName;
  }
}